import React from "react";
import BodyCompanions from "@/components/BodyCompanions";
import SkinEssentialsRitual from "@/components/SkinEssentialsRitual";
import { Waves, Sparkles } from "lucide-react";
import { HERO } from "@/lib/heroImages";
import PageHero from "@/components/PageHero";

/**
 * BodyFaceEssentials — the renovated Body → Essentials experience.
 *
 * BODY  → editorial hero + curated MOVE · NOURISH · RESTORE · CARE progression
 *          rendered inside <BodyCompanions scope="body" /> (which now organizes
 *          its own sections under those eyebrows).
 * FACE  → editorial hero + DAILY CARE (SkinEssentialsRitual) and
 *          MASSAGE & MOVEMENT (BodyCompanions face → Lymphatic).
 *
 * No content is duplicated; nothing user-data-facing is moved or reset.
 */
const BODY_HERO_IMAGE = HERO.body;
const FACE_HERO_IMAGE = HERO.face;

export default function BodyFaceEssentials() {
  const [tab, setTab] = React.useState(() => {
    try { return sessionStorage.getItem("rituals-body-face-tab") || "body"; }
    catch { return "body"; }
  });
  const setAndPersist = (t) => {
    setTab(t);
    try { sessionStorage.setItem("rituals-body-face-tab", t); } catch { /* noop */ }
  };

  return (
    <div className="space-y-10" data-testid="body-face-essentials">
      <div className="max-w-2xl reveal reveal-1" data-testid="body-face-intro">
        <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))]">
          Body
        </div>
        <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight italic leading-tight">
          Care for the body that carries you.
        </h2>
        <p className="mt-4 text-[15px] leading-relaxed text-[hsl(var(--fg))]/80">
          Your body is where you experience your life. These rituals are small ways of supporting its
          strength, mobility, energy, recovery, nourishment, and care — not from a place of fixing it,
          but from a place of living well within it.
        </p>
        <p className="mt-3 text-sm italic text-[hsl(var(--muted-foreground))]">
          Care for your body as a relationship, not a project.
        </p>
      </div>

      <div
        className="inline-flex items-center rounded-full border hairline bg-[hsl(var(--paper))] p-1"
        role="tablist"
        aria-label="Body or Face"
        data-testid="body-face-tabs"
      >
        <SegButton active={tab === "body"} onClick={() => setAndPersist("body")}
          label="Body" hint="Whole-body care" testid="body-face-tab-body" />
        <SegButton active={tab === "face"} onClick={() => setAndPersist("face")}
          label="Face" hint="Facial care" testid="body-face-tab-face" />
      </div>

      {tab === "body" && (
        <div className="space-y-10" data-testid="body-face-body-panel">
          <PageHero
          emblem=""
            eyebrow="Body · Move · Nourish · Restore · Care"
            title="This body carries me through my life."
            body="You don't need every ritual every day. Choose what supports the way you want to care for yourself today."
            image={BODY_HERO_IMAGE}
            testid="body-face-body-hero"
          />
          <BodyCompanions scope="body" />
        </div>
      )}

      {tab === "face" && (
        <div className="space-y-12" data-testid="body-face-face-panel">
          <PageHero
          emblem=""
            eyebrow="Face"
            title="Care for the face that meets the world."
            supporting="Not anti-aging. Intentional aging."
            body="Your face changes with you. These rituals are about caring for your skin, supporting its vitality, and creating moments of attention for the face that has carried every version of you."
            image={FACE_HERO_IMAGE}
            testid="body-face-face-hero"
          />

          {/* DAILY CARE — the existing Skin Essentials routine */}
          <section data-testid="face-daily-care">
            <CategoryEyebrow Icon={Sparkles} label="Daily Care" testid="face-cat-daily" />
            <SkinEssentialsRitual />
          </section>

          {/* MASSAGE & MOVEMENT — the existing Lymphatic Massage Flow */}
          <section data-testid="face-massage-movement">
            <CategoryEyebrow Icon={Waves} label="Massage & Movement" testid="face-cat-massage" />
            <BodyCompanions scope="face" />
          </section>
        </div>
      )}
    </div>
  );
}

function CategoryEyebrow({ Icon, label, testid }) {
  return (
    <div className="mb-6 flex items-center gap-3" data-testid={testid}>
      <Icon size={12} className="text-[hsl(var(--copper))]" />
      <span className="text-[11px] uppercase tracking-[0.32em] text-[hsl(var(--copper))]">
        {label}
      </span>
      <span aria-hidden className="flex-1 h-px bg-[hsl(var(--line))]" />
    </div>
  );
}

function SegButton({ active, onClick, label, hint, testid }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      className={`px-5 py-2 rounded-full text-[12px] uppercase tracking-[0.24em] transition-all ${
        active ? "bg-[hsl(var(--copper))] text-[hsl(var(--paper))]"
               : "text-[hsl(var(--fg))]/70 hover:text-[hsl(var(--copper))]"
      }`}
    >
      <span>{label}</span>
      {hint && (
        <span className={`ml-2 text-[10px] tracking-[0.16em] ${
          active ? "text-[hsl(var(--paper))]/70" : "text-[hsl(var(--muted-foreground))]"
        }`}>
          · {hint}
        </span>
      )}
    </button>
  );
}
